import React, { SetStateAction, useState, useEffect } from 'react';
import { Alert, Button } from 'aha-components';
import WebModal from '@/components/web/components/Modal';
import Checkbox from '@material-ui/core/Checkbox';
import { getOrderId, isMobile } from '@/biz/tool';
import TextareaAutosize from '@material-ui/core/TextareaAutosize';
import axiosInstance from '@/apis/axiosInstance';
import toast from 'react-hot-toast';
import { OrderStatus } from '@/interface/order';
import { useDetailDataStore } from '@/hooks/useDetailDataStore';
import { useWebModalStore } from '@/hooks/useWebModalStore';
import BottomDrawer from '@/components/web/components/BottomDrawer';
import { useTranslation } from 'react-i18next';

interface Props {
  renderTrigger?: (open: () => void, loading: boolean) => React.ReactNode;
  onSuccess?: () => void;
}

const ModalNotInterest: React.FC<Props> = ({ renderTrigger, onSuccess }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [feedback, setFeedback] = useState([]);
  const [submitBtnLoading, setSubmitBtnLoading] = useState(false);
  const [showPriceInput, setShowPriceInput] = useState(false);
  const [showTimelineInput, setShowTimelineInput] = useState(false);
  const [showOtherInput, setShowOtherInput] = useState(false);
  const { detailData, duringFirstOrder, reQueryDetailData } =
    useDetailDataStore();
  const { setShowNotInterestSubmitModal, setShowModalRefuseCampaign } =
    useWebModalStore();
  const { order, campaign } = detailData;

  const updateFeedback = (
    feedback: Array<any>,
    type: string,
    input: string,
    isSelect: boolean,
    index: number
  ) => {
    if (isSelect) {
      feedback[index] = { type, input };
    } else {
      feedback[index] = false;
    }

    setFeedback([...feedback] as SetStateAction<never[]>);
  };

  const submitFeedback = async () => {
    if (submitBtnLoading) return;
    if (!feedback.some(Boolean)) {
      toast.error(
        t('modal.not_interest.feedback.error', {
          defaultValue: 'Please select at least one reason.'
        })
      );
      return;
    }
    const orderId = getOrderId();
    setSubmitBtnLoading(true);
    try {
      if (!!order?.ahaPicksOffer) {
        await axiosInstance.post(`/order/${orderId}/ahaPicksOffer/respond`, {
          action: 'decline',
          reasons: feedback.filter((item) => !!item)
        });
      } else {
        await axiosInstance.post(
          `/order/${orderId}/creatorReject`,
          feedback.filter((item) => !!item)
        );
      }
      toast.success(
        t('modal.not_interest.feedback.success', {
          defaultValue: 'Submission success.'
        })
      );
      setShowModalRefuseCampaign(true);
      //成功之后关闭弹窗，并且隐藏按钮
      setIsOpen(false);
      onSuccess?.();
      // 提交后刷新页面数据
      reQueryDetailData({ orderId });
    } catch (err) {
      toast.error(
        t('modal.not_interest.feedback.failed', {
          defaultValue: 'Submission failed.'
        })
      );
      console.error(err);
    } finally {
      setSubmitBtnLoading(false);
      // 首单逻辑 01
      if (duringFirstOrder) {
        setShowNotInterestSubmitModal(true);
      }
    }
  };
  let WebModalAndMobileDraw = WebModal;
  if (isMobile()) {
    WebModalAndMobileDraw = BottomDrawer;
  }
  if (![OrderStatus.Invited].includes(order?.status)) {
    return null;
  }

  const openReasonModal = () => {
    setFeedback([]);
    setIsOpen(true);
    setShowPriceInput(false);
    setShowTimelineInput(false);
    setShowOtherInput(false);
  };

  return (
    <>
      {renderTrigger ? (
        renderTrigger(openReasonModal, submitBtnLoading)
      ) : (
        <Button
          disabled={submitBtnLoading}
          className={`!w-full !border-none`}
          onClick={openReasonModal}
        >
          {!!order?.ahaPicksOffer
            ? t('modal.not_interest.decline_offer', {
                defaultValue: 'Decline offer'
              })
            : t('modal.not_interest.button', { defaultValue: 'Pass for now' })}
        </Button>
      )}
      <WebModalAndMobileDraw
        title={t('modal.not_interest.title', {
          defaultValue: 'Pass for now'
        })}
        {...(renderTrigger
          ? isMobile()
            ? { rootClassName: '!z-[10001]' }
            : { modalStyle: { zIndex: 10001 } }
          : {})}
        visible={isOpen}
        submitDisabled={false}
        isLoading={submitBtnLoading}
        handleClose={() => {
          setIsOpen(false);
        }}
        handleSubmit={() => {
          submitFeedback();
        }}
        width={isMobile() ? 342 : 624}
      >
        <div className="max-h-[70vh] flex flex-col gap-3 w-full overflow-y-auto overflow-x-visible">
          <div className=" text-[#60646c] text-xs text-left w-full font-normal font-['Inter'] leading-tight">
            {order?.isCoreMatch && !!order?.ahaPicksOffer
              ? t('modal.not_interest.description_core_match', {
                  defaultValue:
                    '{{brand_name}} picked you for this one. A quick reason keeps the door open for future collabs and sharpens the deals we send you next.',
                  brand_name: campaign?.productName
                })
              : t('modal.not_interest.description', {
                  defaultValue:
                    "We're sorry that you're not interested. Help us improve this deal by telling us why:"
                })}
          </div>
          <Alert
            type="info"
            description={t('modal.not_interest.shared_with_brand', {
              defaultValue: 'Your reason will be shared with {{brand_name}}.',
              brand_name: campaign?.productName
            })}
          />
          <div
            className={`w-full
          ${isMobile() ? '' : 'ml-[0px]'}`}
          >
            <div className="flex items-center">
              <Checkbox
                color="primary"
                onChange={(_, isSelect) => {
                  updateFeedback(
                    feedback,
                    t('modal.not_interest.reason.price', {
                      defaultValue: 'Proposed price is not attractive'
                    }),
                    '',
                    isSelect,
                    0
                  );
                  setShowPriceInput(!showPriceInput);
                }}
              />
              {t('modal.not_interest.reason.price', {
                defaultValue: 'Proposed price is not attractive'
              })}
            </div>
            {showPriceInput ? (
              <div className="flex flex-col items-start border border-zinc-200 rounded-lg p-1  gap-2 ml-10  mb-2">
                <TextareaAutosize
                  minRows={1}
                  className="w-full pl-2 py-2 rounded-md placeholder:text-[#71717a] placeholder:text-sm "
                  onChange={(event) => {
                    updateFeedback(
                      feedback,
                      t('modal.not_interest.reason.price', {
                        defaultValue: 'Proposed price is not attractive'
                      }),
                      event.target.value,
                      true,
                      0
                    );
                  }}
                  placeholder={t('modal.not_interest.placeholder.price', {
                    defaultValue: 'Enter your expected price'
                  })}
                />
              </div>
            ) : null}
            <div className="flex  items-center">
              <Checkbox
                color="primary"
                onChange={(_, isSelect) => {
                  updateFeedback(
                    feedback,
                    t('modal.not_interest.reason.timeline', {
                      defaultValue:
                        'Timeline is too tight given the deliverables'
                    }),
                    '',
                    isSelect,
                    1
                  );
                  setShowTimelineInput(!showTimelineInput);
                }}
              />
              {t('modal.not_interest.reason.timeline', {
                defaultValue: 'Timeline is too tight given the deliverables'
              })}
            </div>
            {showTimelineInput ? (
              <div className="flex flex-col items-start border border-zinc-200 rounded-lg p-1  gap-2 ml-10  mb-2">
                <TextareaAutosize
                  minRows={1}
                  className="w-full pl-2 py-2 rounded-md placeholder:text-[#71717a] placeholder:text-sm "
                  onChange={(event) => {
                    updateFeedback(
                      feedback,
                      t('modal.not_interest.reason.timeline', {
                        defaultValue:
                          'Timeline is too tight given the deliverables'
                      }),
                      event.target.value,
                      true,
                      1
                    );
                  }}
                  placeholder={t('modal.not_interest.placeholder.timeline', {
                    defaultValue:
                      'Enter your expected timeline. (eg: 2 days for filming, 3 days for editing)'
                  })}
                />
              </div>
            ) : null}
            <div className="flex  items-center">
              <Checkbox
                color="primary"
                onChange={(_, isSelect) => {
                  updateFeedback(
                    feedback,
                    t('modal.not_interest.reason.content_fit', {
                      defaultValue:
                        'This product does not fit my content/channel'
                    }),
                    '',
                    isSelect,
                    2
                  );
                }}
              />
              {t('modal.not_interest.reason.content_fit', {
                defaultValue: 'This product does not fit my content/channel'
              })}
            </div>
            <div className="flex  items-center">
              <Checkbox
                color="primary"
                onChange={(_, isSelect) => {
                  updateFeedback(
                    feedback,
                    t('modal.not_interest.reason.other', {
                      defaultValue: 'Other'
                    }),
                    '',
                    isSelect,
                    3
                  );
                  setShowOtherInput(!showOtherInput);
                }}
              />
              {t('modal.not_interest.reason.other', { defaultValue: 'Other' })}
            </div>
            {showOtherInput ? (
              <div className="flex flex-col items-start border border-zinc-200 rounded-lg p-1 gap-2 ml-10 mb-2">
                <TextareaAutosize
                  minRows={3}
                  className="w-full pl-2 py-2 rounded-md placeholder:text-[#71717a] placeholder:text-sm "
                  onChange={(event) =>
                    updateFeedback(
                      feedback,
                      t('modal.not_interest.reason.other', {
                        defaultValue: 'Other'
                      }),
                      event.target.value,
                      true,
                      3
                    )
                  }
                  placeholder={t('modal.not_interest.placeholder.other', {
                    defaultValue:
                      'Enter your reason. (eg: I have scheduling conflicts)'
                  })}
                />
              </div>
            ) : null}
          </div>
        </div>
      </WebModalAndMobileDraw>
    </>
  );
};

export default ModalNotInterest;
